/* Created by Interactive 63 - https://interactive63.com */
/**
 * Assembles final export files from a compiled scene: a full HTML page, an embeddable snippet,
 * a standalone animated SVG and a zip of the lot. All functions are pure.
 */
import { strToU8, zipSync } from 'fflate';
import type { ExportSettings } from '../model/types';
import { CREDIT_MARKUP } from './minify';
import { serialiseConfigForHtml } from './runtime-config';
import type { CompiledScene } from './types';

export type PreviewBackground = 'white' | 'dark' | 'checker';

export interface DocumentOptions {
  /** Page background, for the preview only. */
  background?: PreviewBackground;
  /** The minified runtime, needed to inline it. Supplied by the editor build. */
  runtimeCode?: string;
  /** Adds the preview-only extras: scroll spacers and the message listener for the API console. */
  preview?: boolean;
}

export const RUNTIME_FILE_NAME = 'svg-tweener-runtime.min.js';

const SVG_NS = 'http://www.w3.org/2000/svg';

const BACKGROUNDS: Record<PreviewBackground, string> = {
  white: '#fff',
  dark: '#1b1b1f',
  checker: 'conic-gradient(#d8d8d8 25%, #fff 0 50%, #d8d8d8 0 75%, #fff 0) 0 0 / 20px 20px',
};

/** Escapes text for use in HTML. */
export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/** The attribute text for the wrapper: its class, then any attributes the compiler asked for. */
function wrapperAttributes(scene: CompiledScene): string {
  const extra = Object.entries(scene.wrapperAttributes).map(([name, value]) =>
    value === '' ? name : `${name}="${escapeHtml(value)}"`,
  );
  return [`class="${escapeHtml(scene.sceneClass)}"`, ...extra].join(' ');
}

/** The config element placed inside the wrapper in CSS + JS mode. */
export function configScript(scene: CompiledScene): string {
  return scene.runtimeConfig
    ? `<script type="application/json" data-svg-tweener-config>${serialiseConfigForHtml(scene.runtimeConfig)}</script>`
    : '';
}

/** The markup placed in a page: the SVG, and its config, inside a wrapper carrying the scene class. */
function wrapped(scene: CompiledScene): string {
  return `${CREDIT_MARKUP}\n<div ${wrapperAttributes(scene)}>${scene.svgMarkup}${configScript(scene)}</div>`;
}

/** The script tag that loads the runtime, inline or from a file next to the page. */
export function runtimeInclude(
  scene: CompiledScene,
  settings: ExportSettings,
  runtimeCode?: string,
): string {
  if (!scene.runtimeConfig) return '';
  if (settings.runtimeDelivery === 'external-file' || runtimeCode === undefined) {
    return `<script src="${RUNTIME_FILE_NAME}" defer></script>`;
  }
  return `<script>\n${runtimeCode}\n</script>`;
}

/** Spacers and a note so scroll-driven triggers can be tested in the small preview frame. */
function previewScrollWrap(scene: CompiledScene, markup: string): string {
  const type = scene.runtimeConfig?.trigger.type;
  if (type !== 'inView' && type !== 'scroll') return markup;
  const spacer =
    '<div style="height:120vh;display:grid;place-items:center;font:14px sans-serif;color:#888">';
  return `${spacer}Scroll to test &darr;</div>\n${markup}\n${spacer}&uarr; Scroll back up</div>`;
}

/** The preview-only listener that lets the API console drive the scene. Never part of an export. */
const PREVIEW_HARNESS = `<script>
addEventListener('message', function (e) {
  var d = e.data, s = window.SvgTweener && window.SvgTweener.scenes()[0];
  if (e.source !== parent || !d || !s) return;
  if (d.t === 'play') s.play(); else if (d.t === 'pause') s.pause();
  else if (d.t === 'reverse') s.reverse(); else if (d.t === 'restart') s.restart();
  else if (d.t === 'seek') s.progress(d.p);
});
</script>`;

/** The styles that centre the scene on a standalone page. A background is added only for the preview. */
function pageStyles(scene: CompiledScene, options: DocumentOptions): string {
  const background = options.background ? ` background: ${BACKGROUNDS[options.background]};` : '';
  return `body { margin: 0; min-height: 100vh; display: grid; place-items: center;${background} }\n.${scene.sceneClass} { width: min(90vw, 640px); }`;
}

/** A complete HTML page with the styles inline. */
export function htmlDocument(
  scene: CompiledScene,
  settings: ExportSettings,
  projectName: string,
  options: DocumentOptions = {},
): string {
  return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(projectName)}</title>
<style>
${pageStyles(scene, options)}
${scene.css.trimEnd()}
</style>
</head>
<body>
${previewBody(scene, settings, options)}
</body>
</html>
`;
}

/** The body of a standalone page: the scene, then the runtime, then preview extras. */
function previewBody(
  scene: CompiledScene,
  settings: ExportSettings,
  options: DocumentOptions,
): string {
  const markup = options.preview ? previewScrollWrap(scene, wrapped(scene)) : wrapped(scene);
  const include = runtimeInclude(scene, settings, options.runtimeCode);
  const harness = options.preview && scene.runtimeConfig ? PREVIEW_HARNESS : '';
  return [markup, include, harness].filter((part) => part !== '').join('\n');
}

export interface EmbedSnippet {
  html: string;
  css: string;
  js: string;
  /** All three parts as one block, ready to paste into a page. */
  combined: string;
}

/** The three parts of an embeddable snippet, and the same as one block. */
export function embedSnippet(
  scene: CompiledScene,
  settings: ExportSettings,
  runtimeCode?: string,
): EmbedSnippet {
  const html = wrapped(scene);
  const css = scene.css;
  const include = runtimeInclude(scene, settings, runtimeCode);
  const js = include === '' ? '' : `${include}\n\n${configScript(scene)}`;
  const combined = `${html}\n<style>\n${css.trimEnd()}\n</style>\n${include === '' ? '' : `${include}\n`}`;
  return { html, css, js, combined };
}

/** Whether the scene can be a standalone SVG file: it must not need JavaScript. */
export function animatedSvgUnavailableReason(scene: CompiledScene): string | null {
  return scene.needsRuntime || scene.runtimeConfig !== null
    ? "This animation uses features that need JavaScript, which SVG files used as images can't run."
    : null;
}

/** A standalone SVG with the CSS inside it, which works as an image or a CSS background. */
export function animatedSvg(scene: CompiledScene): string {
  const doc = new DOMParser().parseFromString(scene.svgMarkup, 'image/svg+xml');
  const root = doc.documentElement;
  const classes = (root.getAttribute('class') ?? '').split(/\s+/).filter((c) => c !== '');
  root.setAttribute('class', [scene.sceneClass, ...classes].join(' '));
  const style = doc.createElementNS(SVG_NS, 'style');
  style.textContent = `\n${scene.css.trimEnd()}\n`;
  // The title stays first so assistive technology finds it before anything else.
  const first = root.firstElementChild;
  if (first?.localName === 'title') first.after(style);
  else root.insertBefore(style, root.firstChild);
  const serialised = new XMLSerializer().serializeToString(root);
  return `${CREDIT_MARKUP}\n${serialised}\n`;
}

/** Warnings that apply only to the animated SVG format. */
export function animatedSvgWarnings(hover: boolean): string[] {
  return hover
    ? ["Hover doesn't work when an SVG is used as an image. Use the embed snippet instead."]
    : [];
}

/** A short plain-text guide to the files in the zip. */
function readme(
  scene: CompiledScene,
  sceneName: string,
  withSvg: boolean,
  withRuntime: boolean,
): string {
  return [
    `${sceneName}: an animated graphic made with SVG Tweener`,
    '',
    'Files',
    '  index.html   A complete page. Open it in a browser to see the animation.',
    '  styles.css   The animation styles. index.html links to this file.',
    ...(withRuntime
      ? [`  ${RUNTIME_FILE_NAME}   The small script that powers triggers and the playback API.`]
      : []),
    ...(withSvg
      ? [`  ${sceneName}.svg   The same animation as a single SVG file. Use it as an image.`]
      : []),
    '',
    'Using it on your own page',
    '  1. Copy the <div class="' + scene.sceneClass + '"> block from index.html into your page.',
    '  2. Copy styles.css into your stylesheet, or link to it from your page.',
    '',
    'Reduced motion',
    '  Visitors who ask their device to reduce motion will see a still pose instead of',
    '  the animation, when the export was made with that option turned on.',
    '',
  ].join('\n');
}

/** A zip with a page, its stylesheet, the SVG file when possible, and a README. */
export function zipBundle(
  scene: CompiledScene,
  settings: ExportSettings,
  projectName: string,
  runtimeCode?: string,
): Uint8Array {
  const sceneName = settings.sceneName || 'scene';
  const withSvg = animatedSvgUnavailableReason(scene) === null;
  const page = `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${escapeHtml(projectName)}</title>
<link rel="stylesheet" href="styles.css">
</head>
<body>
${[wrapped(scene), runtimeInclude(scene, settings, runtimeCode)].filter((part) => part !== '').join('\n')}
</body>
</html>
`;
  const styles = `${pageStyles(scene, {})}\n${scene.css}`;
  const files: Record<string, Uint8Array> = {
    [`${sceneName}/index.html`]: strToU8(page),
    [`${sceneName}/styles.css`]: strToU8(styles),
    [`${sceneName}/README.txt`]: strToU8(
      readme(
        scene,
        sceneName,
        withSvg,
        scene.runtimeConfig !== null && settings.runtimeDelivery === 'external-file',
      ),
    ),
  };
  if (withSvg) files[`${sceneName}/${sceneName}.svg`] = strToU8(animatedSvg(scene));
  if (scene.runtimeConfig && settings.runtimeDelivery === 'external-file' && runtimeCode) {
    files[`${sceneName}/${RUNTIME_FILE_NAME}`] = strToU8(runtimeCode);
  }
  return zipSync(files);
}
